import { Button } from "@/components/ui/button";
import { Briefcase, CheckCircle, Heart } from "lucide-react";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";

export default function JoinOurTeam() {
  const arpBenefits = [
    "Reducing stress and anxiety",
    "Improving cardiovascular health",
    "Lowering depression",
    "Providing a sense of accomplishment",
    "Making life more fun!",
  ];

  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Join Our Team | Pet Care Careers in Tallahassee"
        description="Join the Tallahassee Lassie Pet Care team! We're looking for reliable, animal-loving pet sitters and dog walkers. Learn about our Active Retiree Program and career opportunities."
        path="/join-our-team"
      />
      <div className="container mx-auto px-4 max-w-5xl">
        <Reveal>
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-full mb-6">
              <Briefcase size={40} />
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6">Join Our Team!</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Thank you for your interest in Tallahassee Lassie Pet Care! We have the following types of employment — permanent regular employees, active retiree program participants (3, 6, or 9 month commitment), and we have employees who work anywhere from 5 hours per week to 36 hours per week. Our longest employee has been with us for over 6 years, and our newest employee has been with us for 1 month.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="bg-card border p-8 md:p-12 shadow-sm mb-16" style={{ borderRadius: "24px 0 24px 0" }}>
            <h2 className="text-2xl font-serif font-bold mb-6">What You'll Receive</h2>
            <ul className="space-y-4">
              {[
                "On-the-job paid training",
                "Incremental raises",
                "Paid holidays",
                "Bonuses",
                "And more!"
              ].map((perk, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="text-secondary mt-1 flex-shrink-0" size={20} />
                  <span className="text-foreground/80">{perk}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal>
          <div className="text-center bg-primary text-white p-12 mb-16" style={{ borderRadius: "40px 0 40px 0" }}>
            <h2 className="text-3xl font-serif font-bold mb-6">Ready to Apply?</h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
              Click below to fill out our application form. We look forward to hearing from you!
            </p>
            <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 h-14 px-10 text-lg">
              <a href="https://form.jotform.com/242937340928161" target="_blank" rel="noopener noreferrer">
                Apply Now
              </a>
            </Button>
          </div>
        </Reveal>

        <Reveal>
          <div className="bg-secondary/5 border border-secondary/20 p-8 md:p-12 mb-16" style={{ borderRadius: "0 24px 0 24px" }}>
            <div className="flex items-center gap-3 mb-6">
              <Heart className="text-secondary" size={28} />
              <h2 className="text-3xl font-serif font-bold">Active Retiree Program</h2>
            </div>
            <p className="text-lg text-foreground/80 leading-relaxed mb-6">
              This program is for retirees to work part-time with our company. Studies have shown that animal companionship, staying active, and working outside your home can all improve both physical health and mental well-being. That is what we strive to provide to our local retirees with our Active Retiree Program.
            </p>
            <p className="text-lg text-foreground/80 leading-relaxed mb-6">
              This program can help our retirees in many ways, such as:
            </p>
            <ol className="space-y-3 mb-8">
              {arpBenefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-secondary text-white rounded-full flex items-center justify-center text-sm font-bold">{i + 1}</span>
                  <span className="text-foreground/80 text-lg">{benefit}</span>
                </li>
              ))}
            </ol>

            <div className="bg-white p-6 border border-secondary/10 mb-6" style={{ borderRadius: "16px 0 16px 0" }}>
              <h3 className="text-xl font-serif font-bold mb-3">The Backstory</h3>
              <p className="text-foreground/80 leading-relaxed">
                Our Active Retiree Program (ARP) was an unintentional addition to our family business! Tallahassee Lassie's owner, Carla, saw how her retired stepfather gained a lot with the care of his own dogs. Walking and caring for them greatly improved his health. In general, pet companionship can reduce stress, improve cardiovascular health, and serve as a protective factor against anxiety and depression. So Carla had one of her big ideas!
              </p>
            </div>

            <div className="bg-white p-6 border border-secondary/10" style={{ borderRadius: "16px 0 16px 0" }}>
              <h3 className="text-xl font-serif font-bold mb-3">Ideal Candidates</h3>
              <p className="text-foreground/80 leading-relaxed">
                This program is perfect for any active retiree with transportation, experience with pets, and anyone looking to enrich their retirement life. ARP Team Members may work as little or as much as they desire. As with all folks on our team, ARP Team Members can select the type/size/location of their clients and set their schedules as needed.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="text-center">
            <img 
              src="https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/8e37e3b3-31c9-47a6-a981-49ae1320d3d2/Green+Yellow+Modern+We%E2%80%99re+Now+Hiring+Facebook+Post.png"
              alt="Now Hiring at Tallahassee Lassie Pet Care"
              className="max-w-md mx-auto w-full shadow-lg"
              style={{ borderRadius: "24px 0 24px 0" }}
            />
          </div>
        </Reveal>
      </div>
    </div>
  );
}
